import { useCallback, useState } from 'react';
import { DropzoneProps } from 'react-dropzone';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { getGoogleDriveDownloadUrl } from 'services/http/http';
import { logError } from 'services/logging/logging';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { FileInput, FileInputErrorCodes, isFileInputValidationError } from 'components/Form/FileInput/FileInput';
import { LoaderResult } from 'components/Form/FileInput/types';
import { FieldsetLegend } from 'components/Form/Input/FieldsetLegend/FieldsetLegend';
import { useSchemableFormFileUploadContext } from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContext';
import { ExternalFilesList } from 'components/SchemableForm/fields/components/ExternalFilesList/ExternalFilesList';
import { Translate } from 'components/Translate/Translate';

import { FileUploadValue } from '../types';

const ACCEPTED_FILE_TYPES: DropzoneProps['accept'] = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
};

type FileUploadFieldEditProps = {
  field: ui.FileUploadField;
  fieldId: string;
  isReadOnly: boolean;
  variant?: 'large';
  value: FileUploadValue;
};

export const FileUploadFieldEdit = ({ field, fieldId, isReadOnly, variant, value }: FileUploadFieldEditProps) => {
  const formState = useFormState();
  const { t } = useTranslation();
  const fileUploadContextValue = useSchemableFormFileUploadContext();
  const { uploadFile } = fileUploadContextValue || {};
  const { setValue } = useFormContext();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const item = service.useItemByFieldPath(fieldId);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const loader = useCallback(
    async (file: File): Promise<LoaderResult<File>> => {
      if (!uploadFile) {
        throw new Error('File upload is not available');
      }
      setUploadError(null);
      const { fileName } = await uploadFile(file);
      return { data: { ...file, name: fileName } };
    },
    [uploadFile],
  );

  const handleLoad = useCallback(
    (serverFile: File, localFile: File) => {
      setValue(
        fieldId,
        {
          file_name: serverFile.name,
          original_name: localFile.name,
          mime_type: localFile.type,
        },
        { shouldDirty: true, shouldValidate: formState.isSubmitted },
      );
    },
    [fieldId, formState.isSubmitted, setValue],
  );

  const handleError = useCallback(
    (error: Error, _file: File) => {
      if (error.message === FileInputErrorCodes.INVALID_FILE_TYPE) {
        setUploadError(t('common:components.fileInput.error.invalidFileType'));
      } else if (error.message === FileInputErrorCodes.TOO_MANY_FILES) {
        setUploadError(t('common:components.fileInput.error.tooManyFiles'));
      } else {
        if (!isFileInputValidationError(error)) {
          logError(error);
        }
        setUploadError(error.message);
      }
    },
    [t],
  );

  const handleClear = useCallback(() => {
    setValue(fieldId, null);
  }, [fieldId, setValue]);

  const isLargeVariant = variant === 'large';
  const validationError =
    isLargeVariant && isWebEDIFormError(item) ? { message: item.description, type: item.type } : undefined;
  const inlineErrors = uploadError ? { message: uploadError, type: 'error' } : validationError;

  return (
    <div className="space-y-2" data-field={fieldId} tabIndex={-1}>
      <FieldsetLegend
        label={field.label}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
        variant={isLargeVariant ? 'large' : 'default'}
        required={field.modality === 'MANDATORY'}
      />

      <ExternalFilesList
        externalFiles={field.type_config.external_files}
        action={{
          label: t('common:form.externalAction.downloadFile'),
          onClick: (file) => {
            const downloadUrl = getGoogleDriveDownloadUrl(file.url);
            window.open(downloadUrl ?? file.url, '_blank');
          },
          RightIcon: ArrowTopRightOnSquareIcon,
        }}
      />

      {!isReadOnly && (
        <FileInput
          loader={loader}
          onLoad={handleLoad}
          onError={handleError}
          onClear={handleClear}
          accept={ACCEPTED_FILE_TYPES}
          defaultFilename={value?.original_name || undefined}
          errors={inlineErrors}
        />
      )}
    </div>
  );
};
